import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScoreHistory } from '../../../hooks/useScoreHistory';

const ScoreHistoryPage = () => {
  const { history, clearHistory, getHistoryByType, getHistoryByExam } = useScoreHistory();
  const [viewMode, setViewMode] = useState<'all' | 'math' | 'reading-writing' | 'mock'>('all');
  const [selectedExam, setSelectedExam] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const filteredHistory = viewMode === 'all' 
    ? history 
    : getHistoryByType(viewMode);

  const examHistory = selectedExam ? getHistoryByExam(selectedExam) : [];

  const uniqueExams = Array.from(new Set(history.map(entry => entry.examName)));

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 90) return 'text-emerald-600';
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 70) return 'text-blue-600';
    if (percentage >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-emerald-50 border-emerald-200';
    if (percentage >= 80) return 'bg-green-50 border-green-200';
    if (percentage >= 70) return 'bg-blue-50 border-blue-200';
    if (percentage >= 60) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const handleClearHistory = () => {
    clearHistory();
    setShowClearConfirm(false);
    setSelectedExam(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link 
                to="/sat-prep" 
                className="inline-flex items-center text-sm text-gray-600 hover:text-indigo-600 mb-2 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                Back to SAT Prep
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">Score History</h1>
              <p className="text-gray-600 mt-1">Track your progress across all practice tests</p>
            </div>
            {history.length > 0 && (
              <button
                onClick={() => setShowClearConfirm(true)}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-delete-bin-line mr-2"></i>
                Clear History
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {history.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-history-line text-5xl text-gray-400"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Score History Yet</h2>
            <p className="text-gray-600 mb-8">Complete practice tests to start tracking your progress</p>
            <Link 
              to="/sat-prep"
              className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-book-open-line mr-2"></i>
              Browse Practice Tests
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Sidebar - Filters */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-24">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Filter by Type</h3>
                <div className="space-y-2">
                  {[
                    { value: 'all', label: 'All Tests', icon: 'ri-list-check' },
                    { value: 'math', label: 'Math Only', icon: 'ri-calculator-line' },
                    { value: 'reading-writing', label: 'Reading & Writing', icon: 'ri-book-line' },
                    { value: 'mock', label: 'Mock Exams', icon: 'ri-timer-line' }
                  ].map(filter => (
                    <button
                      key={filter.value}
                      onClick={() => {
                        setViewMode(filter.value as any);
                        setSelectedExam(null);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                        viewMode === filter.value
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i className={`${filter.icon} mr-2`}></i>
                      {filter.label}
                    </button>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Stats</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Attempts</span>
                      <span className="text-lg font-bold text-gray-900">{history.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Average Score</span>
                      <span className="text-lg font-bold text-indigo-600">
                        {Math.round(history.reduce((sum, entry) => sum + entry.percentage, 0) / history.length)}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Best Score</span>
                      <span className="text-lg font-bold text-emerald-600">
                        {Math.max(...history.map(entry => entry.percentage))}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2">
              {selectedExam ? (
                <div>
                  <button
                    onClick={() => setSelectedExam(null)}
                    className="inline-flex items-center text-sm text-gray-600 hover:text-indigo-600 mb-4 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line mr-1"></i>
                    Back to All History
                  </button>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedExam}</h2>
                    <p className="text-gray-600 mb-6">{examHistory.length} attempts</p>

                    {/* Progress Chart */}
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Score Progression</h3>
                      <div className="relative h-64 bg-gray-50 rounded-lg p-4">
                        <svg className="w-full h-full" viewBox="0 0 800 200">
                          {/* Grid lines */}
                          {[0, 25, 50, 75, 100].map(y => (
                            <g key={y}>
                              <line
                                x1="40"
                                y1={180 - (y * 1.6)}
                                x2="760"
                                y2={180 - (y * 1.6)}
                                stroke="#e5e7eb"
                                strokeWidth="1"
                              />
                              <text
                                x="10"
                                y={185 - (y * 1.6)}
                                fontSize="12"
                                fill="#6b7280"
                              >
                                {y}%
                              </text>
                            </g>
                          ))}

                          {/* Line chart */}
                          {examHistory.length > 1 && (
                            <polyline
                              points={examHistory
                                .slice()
                                .reverse()
                                .map((entry, idx) => {
                                  const x = 40 + (idx * (720 / (examHistory.length - 1)));
                                  const y = 180 - (entry.percentage * 1.6);
                                  return `${x},${y}`;
                                })
                                .join(' ')}
                              fill="none"
                              stroke="#6366f1"
                              strokeWidth="3"
                            />
                          )}

                          {/* Data points */}
                          {examHistory.slice().reverse().map((entry, idx) => {
                            const x = examHistory.length === 1 
                              ? 400 
                              : 40 + (idx * (720 / (examHistory.length - 1)));
                            const y = 180 - (entry.percentage * 1.6);
                            return (
                              <g key={entry.id}>
                                <circle
                                  cx={x}
                                  cy={y}
                                  r="6"
                                  fill="#6366f1"
                                  stroke="white"
                                  strokeWidth="2"
                                />
                                <text
                                  x={x}
                                  y={y - 15}
                                  fontSize="12"
                                  fill="#6366f1"
                                  fontWeight="bold"
                                  textAnchor="middle"
                                >
                                  {entry.percentage}%
                                </text>
                              </g>
                            );
                          })}
                        </svg>
                      </div>
                    </div>

                    {/* Attempt List */}
                    <div className="space-y-3">
                      {examHistory.map((entry, idx) => (
                        <div
                          key={entry.id}
                          className={`p-4 rounded-lg border ${getScoreBgColor(entry.percentage)}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-gray-700">
                                  Attempt #{examHistory.length - idx}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatDate(entry.timestamp)}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600">
                                {entry.score} / {entry.total} correct
                              </div>
                            </div>
                            <div className={`text-3xl font-bold ${getScoreColor(entry.percentage)}`}>
                              {entry.percentage}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {viewMode === 'all' ? 'All Attempts' : 
                     viewMode === 'math' ? 'Math Practice Tests' :
                     viewMode === 'reading-writing' ? 'Reading & Writing Tests' :
                     'Mock Exams'}
                  </h2>

                  {filteredHistory.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                      <i className="ri-inbox-line text-5xl text-gray-300 mb-4"></i>
                      <p className="text-gray-600">No attempts found for this filter</p>
                    </div>
                  ) : (
                    filteredHistory.map(entry => (
                      <div
                        key={entry.id}
                        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-bold text-gray-900">{entry.examName}</h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                entry.examType === 'math' 
                                  ? 'bg-blue-100 text-blue-700'
                                  : entry.examType === 'reading-writing'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-indigo-100 text-indigo-700'
                              }`}>
                                {entry.examType === 'math' ? 'Math' : 
                                 entry.examType === 'reading-writing' ? 'Reading & Writing' : 
                                 'Mock Exam'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{entry.domain}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>
                                <i className="ri-calendar-line mr-1"></i>
                                {formatDate(entry.timestamp)}
                              </span>
                              <span>
                                <i className="ri-file-list-line mr-1"></i>
                                {entry.score} / {entry.total} correct
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-4xl font-bold mb-2 ${getScoreColor(entry.percentage)}`}>
                              {entry.percentage}%
                            </div>
                            <button
                              onClick={() => setSelectedExam(entry.examName)}
                              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer whitespace-nowrap"
                            >
                              View Progress
                              <i className="ri-arrow-right-line ml-1"></i>
                            </button>
                          </div>
                        </div>

                        {/* Score bar */}
                        <div className="mt-4 bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              entry.percentage >= 90 ? 'bg-emerald-500' :
                              entry.percentage >= 80 ? 'bg-green-500' :
                              entry.percentage >= 70 ? 'bg-blue-500' :
                              entry.percentage >= 60 ? 'bg-amber-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${entry.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-alert-line text-3xl text-red-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Clear All History?</h3>
            <p className="text-gray-600 text-center mb-6">
              This will permanently delete all {history.length} score records. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleClearHistory}
                className="flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear History
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoreHistoryPage;